<template>
  <section class="analytics-section" v-if="canLoadWidgets" :key="'billing-charge-analytics'">
    <section class="mb-3">
      <div>
        <b-row class="align-items-center">
          <b-col cols="12" md="auto" class="mb-2 mb-lg-0">
            <h3>Analytics - Client Billing Stats</h3>
            <p class="text-muted mb-0">
              Analyze billing charges including surcharges/accessorials and freight charges by carrier, service, and zone
            </p>
          </b-col>
          <b-col cols="12" md="5" class="mb-2 mb-lg-0">
            <ActiveFiltersCard
              :items-filters="filters"
              :load-items="applyFilters"
              :reset-filter-with-key="resetFilterWithKey"
            />
          </b-col>
          <b-col cols="12" md="auto" class="text-lg-right ml-auto">
            <b-button
              variant="primary"
              @click="showFilterAside = true"
            >
              <i class="fa fa-filter"></i> Filters
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Filters Aside -->
      <b-sidebar
        v-model="showFilterAside"
        title="Filters"
        right
        shadow
        backdrop
      >
        <div class="px-3 py-2">
          <b-form-group label="For Client:" label-for="filter-brand-id">
            <b-v-select
              id="filter-brand-id"
              v-model="filterForm.brandId"
              :searchable="true"
              :options="brandsFilter"
            />
          </b-form-group>

          <b-form-group label="From Warehouse:" label-for="filter-warehouse-id" class="mt-3">
            <b-v-select
              id="filter-warehouse-id"
              v-model="filterForm.warehouseId"
              :searchable="true"
              :options="warehousesFilter"
            />
          </b-form-group>

          <b-form-group label="Charge Label:" label-for="filter-charge-label" class="mt-3">
            <b-form-input
              id="filter-charge-label"
              v-model="filterForm.chargeLabel"
              placeholder="Filter by charge label..."
            ></b-form-input>
          </b-form-group>

          <b-form-group label="From Date" label-for="filter-from-date" class="mt-3">
            <b-form-datepicker
              id="filter-from-date"
              v-model="filterForm.fromDate"
              :max="filterForm.toDate"
            ></b-form-datepicker>
          </b-form-group>

          <b-form-group label="To Date" label-for="filter-to-date" class="mt-3">
            <b-form-datepicker
              id="filter-to-date"
              v-model="filterForm.toDate"
              :min="filterForm.fromDate"
            ></b-form-datepicker>
          </b-form-group>

          <div class="mt-4">
            <b-button
              variant="primary"
              block
              @click="applyFilters"
            >
              Apply Filters
            </b-button>
          </div>
        </div>
      </b-sidebar>

      <div class="animated fadeIn pt-3">
        <b-tabs content-class="mt-0 p-0">

          <b-tab title="By Carrier">
            <b-row>
              <b-col sm="12">
                <ChartBillingChargeStats
                  group-by="bycarrier"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-label="filters.chargeLabel"
                  :charge-type="filters.chargeType"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>
          
          <b-tab title="By Surcharge/Accessorial" active>
            <b-row>
              <b-col sm="12">
                <ChartBillingChargeStats
                  group-by="bysurcharge"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-label="filters.chargeLabel"
                  :charge-type="filters.chargeType"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Service">
            <b-row>
              <b-col sm="12">
                <ChartBillingChargeStats
                  group-by="byservice"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-label="filters.chargeLabel"
                  :charge-type="filters.chargeType"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Zone">
            <b-row>
              <b-col sm="12">
                <ChartBillingChargeStats
                  group-by="byzoneid"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-label="filters.chargeLabel"
                  :charge-type="filters.chargeType"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Avg Accessorial">
            <b-row>
              <b-col sm="12">
                <ChartBillingChargeStats
                  group-by="byavgaccessorial"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-label="filters.chargeLabel"
                  :charge-type="filters.chargeType"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Fuel Surcharge">
            <b-row>
              <b-col sm="12">
                <ChartBillingChargeStats
                  group-by="byfuel"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-label="filters.chargeLabel"
                  :charge-type="filters.chargeType"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Client">
            <b-row>
              <b-col sm="12">
                <ChartBillingChargeStats
                  group-by="byclient"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-label="filters.chargeLabel"
                  :charge-type="filters.chargeType"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Charge Type">
            <b-row>
              <b-col sm="12">
                <ChartBillingChargeStats
                  group-by="bychargetype"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :charge-label="filters.chargeLabel"
                  :charge-type="filters.chargeType"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartBillingChargeStats>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </div>
    </section>
  </section>
</template>

<script>
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import ChartBillingChargeStats from '@/views/components/partials/analytics/ChartBillingChargeStats.vue';
import ActiveFiltersCard from '@/views/components/ActiveFiltersCard.vue';

export default {
  name: 'BillingChargeStats',
  components: {
    ChartBillingChargeStats,
    ActiveFiltersCard
  },
  mixins: [myCommonDataMixin],
  data: function() {
    var todaysDate = new Date();
    var lastQuarterFromDate = new Date();
    lastQuarterFromDate.setMonth(todaysDate.getMonth() - 3);

    return {
      canLoadWidgets: false,
      showFilterAside: false,
      filterForm: {
        brandId: '',
        warehouseId: '',
        chargeLabel: '',
        chargeType: '',
        fromDate: lastQuarterFromDate.toISOString().split('T')[0],
        toDate: todaysDate.toISOString().split('T')[0]
      },
      filters: {
        brandId: '',
        warehouseId: '',
        chargeLabel: '',
        chargeType: '',
        fromDate: lastQuarterFromDate.toISOString().split('T')[0],
        toDate: todaysDate.toISOString().split('T')[0]
      },
      chargeTypeOptions: [
        { value: '', text: 'All Charge Types' },
        { value: 'ConnectAdministrative', text: 'Administrative' },
        { value: 'TPLHandling', text: 'WMS - Handling' },
        { value: 'TPLStorage', text: 'WMS - Storage' },
        { value: 'TPLPrepaidFreight', text: 'WMS - Prepaid Freight' },
        { value: 'TPLThirdPartyFreight', text: 'WMS - Third Party Freight' },
        { value: 'TPLSpecialCharges', text: 'WMS - Special Charges' },
        { value: 'TPLMaterials', text: 'WMS - Materials' },
        { value: 'TPLAutoCalcStorage', text: 'WMS - AutoCalc Storage' },
        { value: 'TPLAutoCalcHandling', text: 'WMS - AutoCalc Handling' },
        { value: 'ConnectAutoCalcPickBin', text: 'Pick Bin Fees' },
        { value: 'ConnectAutoCalcStorage', text: 'Storage Fees' },
        { value: 'ConnectOrderReturn', text: 'Connect - Order Return' },
        { value: 'ConnectOrderItemReturn', text: 'Connect - Order Item Returned' },
        { value: 'Freight via Connect', text: 'Freight + Fuel (Connect)' },
        { value: 'ConnectFreightCharge', text: 'Base Freight (Connect)' },
        { value: 'ConnectFreightFuelSurchargeCharge', text: 'Fuel Surcharges (Connect)' },
        { value: 'FreightAdjustment', text: 'Freight Accessorials' },
        { value: 'Other', text: 'Other' }
      ]
    };
  },
  mounted() {
    this.canLoadWidgets = true;
  },
  methods: {
    applyFilters: function() {
      this.filters = Object.assign({}, this.filterForm);
      this.showFilterAside = false;
    },
    resetFilterWithKey: function(key) {
      this.filterForm[key] = '';
      this.filters[key] = '';
    }
  }
};
</script>
